<template>
  <div>
    <template v-if="data">
      <template v-if="cleanedHeader === 'standardDark' || cleanedHeader === 'standardWhite'">
        <UtilityNavbar
          :create-student-account-button="data.createStudentAccountButton"
          :signin-button="data.signinButton"
          :signup-button="data.signupButton"
          :customer-home="data.customerHome"
          :open-onshape="data.openOnshape"
        />
        <Header
          v-bind="data"
          :light="isHeaderLight"
          :class="{
            'standard-header': true,
          }"
        />
        <ClientOnly>
          <template v-if="hasUssForm">
            <LpHeader
              :light="false"
              v-bind="data"
              :class="{ 'uss-lp-header': true }"
            />
            <HekateProgressBar />
            <HekateLoader />
          </template>
        </ClientOnly>

        <main
          :id="ussFormContainer"
          :class="{ 'uss-form-container mb-0': true }"
        >
          <slot />
        </main>
      </template>

      <template v-else-if="cleanedHeader === 'lpDark' || cleanedHeader === 'lpWhite'">
        <div class="lp-header-wrapper">
          <div class="uss-lp-absolute-header absolute z-20 w-full">
            <LpHeader
              :light="isHeaderLight"
              v-bind="data"
            />
          </div>
          <ClientOnly>
            <template v-if="hasUssForm">
              <LpHeader
                :light="false"
                v-bind="data"
                :class="{ 'uss-lp-header': true }"
              />
              <HekateProgressBar />
              <HekateLoader />
            </template>
          </ClientOnly>
          <main
            :id="ussFormContainer"
            :class="{ 'uss-form-container mb-0': true }"
          >
            <slot />
          </main>
        </div>
      </template>

      <!-- Case of Enterprise pages with no header -->
      <template v-else-if="cleanedHeader === 'none'">
        <main>
          <slot />
        </main>
      </template>

      <template v-if="cleanedFooter !== 'none'">
        <Footer
          v-if="footerData && cleanedFooter === 'standard'"
          v-bind="footerData"
        />
        <LpFooter
          v-else-if="footerData && cleanedFooter === 'lp'"
          v-bind="footerData"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { stegaClean } from '@sanity/client/stega';
import type { Generic, Header, Footer } from '~/sanity/types';

const hasUssForm = useState('has-uss-form', () => false);

const ussFormContainer = ref('');
watchEffect(
  () => {
    ussFormContainer.value = hasUssForm.value ? 'uss-form-container' : '';
  },
  {
    flush: 'post',
  }
);
interface Props {
  header?: Generic['header'];
  footer?: Generic['footer'];
}
const { header = 'standardDark', footer = 'standard' } = defineProps<Props>();
const cleanedHeader = stegaClean(header).trim();
const cleanedFooter = stegaClean(footer).trim();

const headerType =
  header === 'standardDark' || header === 'standardWhite' ? 'header' : 'landing-page-header';
const footerType = footer === 'standard' ? 'footer' : 'lp';

const query = groq`*[ _type == "header" && slug.current == $headerType][0]`;
const { data } = await useWwwSanityQuery<Header>(query, {
  headerType,
});

const queryFooter = groq`*[ _type == "footer" && slug.current == $footerType ][0]`;
const { data: footerData } = await useWwwSanityQuery<Footer>(queryFooter, {
  footerType,
});

const isHeaderLight = computed(() => {
  if (header === 'standardWhite' || header === 'lpWhite') {
    return true;
  }
  return false;
});
</script>

<style>
.uss-lp-header .logo {
  margin-top: 50px !important;
}
.hekate-uss .uss-form-container h2 {
  color: var(--www-color-neutral-medium-dark);
  font-size: 20px !important;
  font-weight: 700 !important;
  line-height: 28.1px !important;
  margin-bottom: 15px !important;
  text-align: center;
}

@media (min-width: 768px) {
  .uss-lp-header img {
    max-width: 170px !important;
    margin-top: 60px !important;
  }
  .hekate-uss .uss-form-container h2 {
    font-size: 24px !important;
  }
}
@media (min-width: 1024px) {
  .uss-lp-header img {
    height: 49px !important;
    min-width: 216px !important;
  }
  .hekate-uss .uss-form-container h2 {
    font-size: 32px !important;
  }
}

@media (max-width: 768px) {
  .uss-lp-header .logo {
    margin-top: 20px !important;
  }
}
</style>
